import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { WidgetService } from '../services/widget.service';
import { AppSessionStorage } from '../utils/app-session-storage-utils';
import { ThemeService } from '../services/theme.service';
import { AlarmService } from '../services/alarm.service';

@Component({
  selector: 'app-layout-curtain',
  templateUrl: './layout-curtain.component.html',
  styleUrls: ['./layout-curtain.component.scss']
})
export class LayoutCurtainComponent {
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();
  @Output() addLayoutStateChanged = new EventEmitter<boolean>();

  contextMenuVisible = false;
  contextMenuX = 0;
  contextMenuY = 0;
  contextMenuLayout: any | null = null;

  newLayoutName: string = '';
  showAddInput: boolean = false;
  layoutSettings: any;
  errorMessage: string = '';

  defaultContextMenuVisible = false;
  defaultContextMenuX = 0;
  defaultContextMenuY = 0;

  // Standard widgets
  standardWidgets = [
    { id: 'alerts', icon: 'bell', title: 'Alerts' },
    { id: 'alarm-tabs', icon: 'tasks', title: 'Alarm Tabs' },
    { id: 'video-feeds', icon: 'video-camera', title: 'Video Feeds' },
    { id: 'single-video-feed', icon: 'play-circle', title: 'Single Video Window' },
    { id: 'insights', icon: 'pie-chart', title: 'General Insights' },
    { id: 'live-incidents', icon: 'line-chart', title: 'Live Incidents' },
    { id: 'send-alarm', icon: 'send', title: 'Send Alarm' },
    { id: 'map', icon: 'map', title: 'Map' },
    { id: 'ask-ramsys', icon: 'question_answer', title: 'Ask RAMsys' },
    { id: 'alarm-progress', icon: 'check-circle-o', title: 'Alarm Progress' },
    { id: 'workflow', icon: 'address-book-o', title: 'Workflow' },
    { id: 'action-history', icon: 'list', title: 'Alarm Details & Action History' },
    { id: 'branch-controls', icon: 'sliders', title: 'Branch Controls' },
    { id: 'tasks', icon: 'address-card-o', title: 'Tasks' },
    { id: 'chat', icon: 'comments', title: 'Chat' },
    { id: 'vnc', icon: 'hand-pointer-o', title: 'VNC' },
    { id: 'zone', icon: 'crop', title: 'Zone' },
    { id: 'controllers', icon: 'cogs', title: 'Controllers' }
  ];

  // Menu items configuration
  menuItems = [
    { id: 'layouts', icon: 'th-large', name: 'Layouts' },
    { id: 'widgets', icon: 'puzzle-piece', name: 'Widgets' },
    { id: 'themes', icon: 'adjust', name: 'Themes' }
  ];

  selectedMenu: string = 'layouts';
  selectedTheme: string = 'dark';
  gridLocked: boolean = false;
  isPrivacyMode: boolean = false;
  themes: any[] = [];

  constructor(
    public widgetService: WidgetService,
    public themeService: ThemeService,
    public alarmService: AlarmService
  ) { }

  async ngOnInit() {
    await this.getWidgets();
    this.getUserLayoutSettings();
    await this.getLayouts();
    this.gridLocked = localStorage.getItem('gridLocked') === 'true';
    this.isPrivacyMode = localStorage.getItem('privacyMode') === 'true';

    // Load themes from ThemeService
    this.themes = this.themeService.getThemes();
    this.selectedTheme = this.themeService.getCurrentTheme();
  }

  getUserLayoutSettings() {
    const user = AppSessionStorage.getUser();
    this.layoutSettings = user?.layoutSettings;
    this.selectedMenu = this.canCustomizeLayout ? 'layouts' : 'themes';
  }

  async getLayouts() {
    this.widgetService.userLayouts = await this.widgetService.getUserLayouts();
  }

  async applyDefaultLayout() {
    let res = await this.widgetService.resetToDefault();
    if (res?.success) {
      this.widgetService.layoutChanged.next(true);
      this.getLayouts();
    } else {
      this.errorMessage = res.errorMessages[0].message;
    }
  }

  async selectLayout(layout: any) {
    if (layout.isActive) {
      return;
    }
    let res = await this.widgetService.setActiveLayout(layout._id);
    if (res?.success) {
      this.widgetService.layoutChanged.next(true);
      this.getLayouts();
    } else {
      this.errorMessage = res.errorMessages[0].message;
    }
  }

  addLayout() {
    this.showAddInput = true;
    this.addLayoutStateChanged.emit(true);
    setTimeout(() => {
      const input = document.getElementById('new-layout-input');
      if (input) (input as HTMLInputElement).focus();
    }, 100);
  }

  // Prevent curtain from closing when add layout is active
  isAddLayoutActive(): boolean {
    return this.showAddInput;
  }

  clearErrorMessage() {
    this.errorMessage = '';
  }

  async confirmAddLayout() {
    const gridLayout = localStorage.getItem('gridLayout');
    if (this.newLayoutName && gridLayout) {
      let res = await this.widgetService.createLayout(this.newLayoutName, gridLayout);
      if (res?.success) {
        this.widgetService.userLayouts.push(res.data);
        this.newLayoutName = '';
        this.showAddInput = false;
        this.addLayoutStateChanged.emit(false);
        this.clearErrorMessage();
      } else {
        this.errorMessage = res.errorMessages[0].message;
      }
    }
  }

  cancelAddLayout() {
    this.newLayoutName = '';
    this.showAddInput = false;
    this.addLayoutStateChanged.emit(false);
    this.clearErrorMessage();
  }

  toggleCurtain() {
    this.open = !this.open;
    if (!this.open) {
      // Close any open context menus
      this.closeContextMenu();
      this.closeDefaultContextMenu();
      this.closed.emit();
      this.clearErrorMessage();
    }
  }

  onRightClick(event: MouseEvent, layout: any) {
    if (layout.isActive) {
      return;
    }
    event.preventDefault();
    this.contextMenuVisible = true;
    this.contextMenuX = event.clientX;
    this.contextMenuY = event.clientY;
    this.contextMenuLayout = layout;
  }

  closeContextMenu() {
    this.contextMenuVisible = false;
    this.contextMenuLayout = null;
  }

  async deleteFromContextMenu() {
    if (this.contextMenuLayout !== null) {
      let res = await this.widgetService.deleteLayout(this.contextMenuLayout._id);
      if (res?.success) {
        this.getLayouts();
      } else {
        this.errorMessage = res.errorMessages[0].message;
      }
      this.closeContextMenu();
    }
  }

  onDefaultRightClick(event: MouseEvent) {
    event.preventDefault();
    this.defaultContextMenuVisible = true;
    this.defaultContextMenuX = event.clientX;
    this.defaultContextMenuY = event.clientY;
  }

  closeDefaultContextMenu() {
    this.defaultContextMenuVisible = false;
  }

  async updateDefaultLayout() {
    const gridLayout = localStorage.getItem('gridLayout');
    if (gridLayout) {
      let res = await this.widgetService.modifyDefaultLayout(JSON.parse(gridLayout));
      if (res?.success) {
        this.getLayouts();
        this.widgetService.layoutChanged.next(true);
      } else {
        this.errorMessage = res.errorMessages[0].message;
      }
    }

    this.closeContextMenu();
  }

  async resetDefaultLayout() {
    let res = await this.widgetService.modifyDefaultLayout([]);
    if (res?.success) {
      this.getLayouts();
      this.widgetService.layoutChanged.next(true);
    } else {
      this.errorMessage = res.errorMessages[0].message;
    }

    this.closeContextMenu();
  }

  get anyLayoutActive(): boolean {
    return this.widgetService.userLayouts?.some(layout => layout.isActive);
  }

  selectMenu(menu: string) {
    this.selectedMenu = menu;
    this.clearErrorMessage();
  }

  get canCustomizeLayout(): boolean {
    return this.layoutSettings?.allowCustomizeLayout ?? true;
  }

  get filteredMenuItems() {
    if (this.canCustomizeLayout) {
      return this.menuItems;
    }

    return this.menuItems.filter(item => item.id === 'themes');
  }

  selectTheme(themeId: string) {
    const selectedTheme = this.themes.find(theme => theme.id === themeId);
    if (selectedTheme) {
      this.selectedTheme = themeId;
      this.themeService.setTheme(themeId);
    }
  }

  // Listen for escape key press
  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: KeyboardEvent) {
    if (this.contextMenuVisible) {
      this.closeContextMenu();
    } else if (this.defaultContextMenuVisible) {
      this.closeDefaultContextMenu();
    } else if (this.showAddInput) {
      this.cancelAddLayout();
    } else if (this.open) {
      this.toggleCurtain();
    }
  }

  async addWidget(widgetType: string) {
    if (widgetType === 'ask-ramsys' && this.alarmService.activeAlarmsTab === 'chatbot') {
      this.errorMessage = `${widgetType} widget is already in the layout`;
      return;
    }

    const currentLayout = JSON.parse(localStorage.getItem('gridLayout') || '[]');
    let layoutItems = currentLayout;

    if (!currentLayout) {
      layoutItems = await this.widgetService.getActiveLayout();
    }

    const existingWidget = layoutItems.find((item: any) => item.id === widgetType);
    if (existingWidget) {
      this.errorMessage = `${widgetType} widget is already in the layout`;
      return;
    }

    // Check mutual exclusivity between single-video-feed and video-feeds
    if (widgetType === 'single-video-feed') {
      const videoFeedsWidget = layoutItems.find((item: any) => item.id === 'video-feeds');
      if (videoFeedsWidget) {
        this.errorMessage = 'Similar widget is already in the layout';
        return;
      }
    } else if (widgetType === 'video-feeds') {
      const singleVideoFeedWidget = layoutItems.find((item: any) => item.id === 'single-video-feed');
      if (singleVideoFeedWidget) {
        this.errorMessage = 'Similar widget is already in the layout';
        return;
      }
    }

    if (widgetType === 'ask-ramsys') {
      this.alarmService.isAskRamsysActive = true;
    }

    // Check if there's enough space for the new widget
    let newWidgetWidth = 5;
    let newWidgetHeight = 5;

    // Set specific dimensions for certain widgets
    if (widgetType === 'alerts') {
      newWidgetWidth = 2;
      newWidgetHeight = 4;
    } else if (widgetType === 'chat') {
      newWidgetWidth = 2;
      newWidgetHeight = 1;
    }

    // Create a grid representation to check for available space
    const grid = Array(12).fill(null).map(() => Array(12).fill(false));

    // Mark occupied cells
    layoutItems.forEach((item: any) => {
      for (let y = item.y; y < item.y + item.h; y++) {
        for (let x = item.x; x < item.x + item.w; x++) {
          if (y < 12 && x < 12) {
            grid[y][x] = true;
          }
        }
      }
    });

    // Check if there's enough space available
    let hasContiguousSpace = false;
    for (let y = 0; y <= 12 - newWidgetHeight; y++) {
      for (let x = 0; x <= 12 - newWidgetWidth; x++) {
        let canFit = true;
        for (let dy = 0; dy < newWidgetHeight; dy++) {
          for (let dx = 0; dx < newWidgetWidth; dx++) {
            if (grid[y + dy][x + dx]) {
              canFit = false;
              break;
            }
          }
          if (!canFit) break;
        }
        if (canFit) {
          hasContiguousSpace = true;
          break;
        }
      }
      if (hasContiguousSpace) break;
    }

    if (!hasContiguousSpace) {
      this.errorMessage = 'Free up space to add this widget';
      return;
    }

    // Add the widget
    this.widgetService.addWidget(widgetType);
    this.clearErrorMessage();
  }

  lockGrid() {
    this.gridLocked = !this.gridLocked;
    this.widgetService.gridLocked.next(this.gridLocked);
    localStorage.setItem('gridLocked', this.gridLocked.toString());
  }

  privacyMode() {
    this.isPrivacyMode = !this.isPrivacyMode;
    this.widgetService.privacyMode.next(this.isPrivacyMode);
    localStorage.setItem('privacyMode', this.isPrivacyMode.toString());
  }

  async getWidgets() {
    this.widgetService.availableWidgets = await this.widgetService.getWidgets();
  }
}
